import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    IconButton,
    Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";

import DefaultApiService from "../../../../../infrastructure/api/default";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const statusColors = {
  1: { color: "warning", text: "در انتظار بررسی" }, // Pending
  2: { color: "info", text: "در حال بررسی" }, // UnderReview
  3: { color: "success", text: "تایید شده" }, // Approved
  4: { color: "error", text: "رد شده" }, // Rejected
  5: { color: "secondary", text: "نیاز به اطلاعات تکمیلی" }, // NeedsMoreInfo
  6: { color: "primary", text: "مدارک تایید شد - در انتظار قرارداد" }, // DocumentsApproved
};

const statusNameMap = {
  Pending: 1,
  UnderReview: 2,
  Approved: 3,
  Rejected: 4,
  NeedsMoreInfo: 5,
  DocumentsApproved: 6,
};

const resolveStatus = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const asNumber = Number(value);
  if (!Number.isNaN(asNumber) && String(value).trim() !== "") return asNumber;
  return statusNameMap[value] ?? null;
};

const ContractorRegistrationPage = ({
  apiBase = "SLN/Management/ContractorRegistration",
  listPath = "/SLN/Management/ContractorRegistration",
}) => {
  const pageInfo = useGetPageInfo();
  const dataList = useLoaderData("root");
  let navigate = useNavigate();

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    data: {},
    cascadeDelete: false,
  });

  const headCells = [
    {
      id: "createOn",
      label: "تاریخ ثبت",
      type: "date",
    },
    {
      id: "firstName",
      label: "نام",
    },
    {
      id: "lastName",
      label: "نام خانوادگی",
    },
    {
      id: "companyName",
      label: "نام شرکت",
    },
    {
      id: "companyNationalId",
      label: "شناسه ملی / نام کاربری",
    },
    {
      id: "mobile",
      label: "موبایل",
    },
    {
      id: "status",
      label: "وضعیت ثبت‌نام",
      // EnhancedTable calls render(cellValue, row)
      render: (value, row) => {
        const statusKey = resolveStatus(value ?? row?.status);
        const status = statusColors[statusKey] || {
          color: "default",
          text: "نامشخص",
        };
        return <Chip label={status.text} color={status.color} size="small" />;
      },
    },
    {
      id: "currentStageTitle",
      label: "مرحله فعلی",
      render: (value, row) => {
        const title =
          value ||
          row?.currentStageTitle ||
          row?.CurrentStageTitle ||
          statusColors[resolveStatus(row?.status)]?.text ||
          "نامشخص";
        const statusKey = resolveStatus(row?.status);
        const tone =
          statusKey === 4
            ? "error"
            : statusKey === 3
            ? "success"
            : statusKey === 6
            ? "primary"
            : statusKey === 2
            ? "info"
            : "warning";
        return (
          <Chip
            label={title}
            color={tone}
            size="small"
            variant="outlined"
            sx={{ maxWidth: 320 }}
          />
        );
      },
    },
    {
      id: "email",
      label: "ایمیل",
    },
  ];

  const onEditClicked = (row) => {
    navigate(`${listPath}/${row.id}`);
  };

  const onViewClicked = (row) => {
    navigate(`${listPath}/${row.id}`);
  };

  const onDeleteClicked = (row) => {
    setDeleteConfirm((old) => ({ ...old, open: true, data: row }));
  };

  const confirmedDelete = () => {
    return DefaultApiService.deleteOneApi(apiBase, {
        id: deleteConfirm.data.id,
        cascadeDelete: deleteConfirm.cascadeDelete,
      }
    );
  };

  const onFetchData = () => {
    navigate(0);
  };

  const actions = [
    {
      tooltip: "مشاهده جزئیات",
      icon: <VisibilityIcon />,
      onClick: onViewClicked,
    },
    {
      tooltip: "ویرایش",
      icon: <EditIcon />,
      onClick: onEditClicked,
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title="لیست درخواست‌های ثبت‌نام پیمانکاران"
            action={
              <Box>
                <Tooltip title="بروزرسانی">
                  <IconButton onClick={onFetchData}>
                    <SyncIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
          <CardContent>
            <EnhancedTable
              headCells={headCells}
              rows={dataList}
              actions={actions}
              onDelete={onDeleteClicked}
              pageInfo={pageInfo}
            />
          </CardContent>
        </Card>
      </Grid>

      <DeleteConfirm
        open={deleteConfirm.open}
        onClose={() =>
          setDeleteConfirm((old) => ({ ...old, open: false, data: {} }))
        }
        onConfirm={confirmedDelete}
        onSuccess={onFetchData}
        cascadeDelete={deleteConfirm.cascadeDelete}
        onChangeCascadeDelete={(value) =>
          setDeleteConfirm((old) => ({ ...old, cascadeDelete: value }))
        }
      />
    </Grid>
  );
};

export default ContractorRegistrationPage;
